import type { ChainKey } from '@infinex/chain-config';

import type { SupportLevelsByChainAddressId } from './actions';
import { makeChainAddressId } from './assets';
import type { AssetMetadataV2 } from './metadataV2';

/**
 * Derive a per-chain-address support level map from a single V2 asset
 * metadata payload. Used by callers that previously depended on the V1
 * asset-config context support-level map.
 *
 * Chains without a `supportLevel` flag are omitted; downstream
 * `checkChainAddressSupportLevel` falls back to `'full'`, matching V1
 * `supportLevelsByChainAddressId` semantics for non-official assets.
 */
export function getSupportLevelsByChainAddressIdFromAssetMetadata(
  metadata: AssetMetadataV2 | null | undefined
): SupportLevelsByChainAddressId {
  if (!metadata) return {};
  const result: SupportLevelsByChainAddressId = {};
  for (const [chainKey, member] of Object.entries(metadata.members)) {
    if (!member) continue;
    const chain = chainKey as ChainKey;
    const supportLevel = metadata.flags?.[chain]?.supportLevel;
    if (supportLevel) {
      result[makeChainAddressId(chain, member.address)] = supportLevel;
    }
  }
  return result;
}

/**
 * Batch variant: derive a per-chain-address support level map by folding
 * every metadata entry in a batch lookup result. Use when you have a
 * `GetAssetMetadataBatchOutput`-shaped record and want a single map keyed
 * by `ChainAddressId` for downstream filtering.
 *
 * Null entries (lookups that did not resolve to a metadata payload) are
 * silently skipped — the per-metadata helper handles that case.
 */
export function getSupportLevelsByChainAddressIdFromAssetMetadataBatch(
  batch: Record<string, AssetMetadataV2 | null> | null | undefined
): SupportLevelsByChainAddressId {
  if (!batch) return {};
  return Object.values(batch).reduce<SupportLevelsByChainAddressId>(
    (acc, metadata) => ({
      ...acc,
      ...getSupportLevelsByChainAddressIdFromAssetMetadata(metadata),
    }),
    {}
  );
}
